import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { siteInfo, testimonials } from '@/mocks/mangaData';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q53u7nahb7382ov6b0';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setSubmitting(false);
      form.reset();
      return;
    }

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后再试' : (serverMsg || '提交失败，请稍后再试'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOSchema
        type="WebPage"
        pageTitle="关于我们 - BL漫画精选"
        pageDescription={`BL漫画精选品牌介绍，${siteInfo.description} 了解我们的品牌故事、读者口碑与联系方式。`}
        pageUrl="/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Brand Intro */}
          <section className="w-full px-4 md:px-6 py-10" aria-label="品牌介绍">
            <div className="max-w-5xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 text-center mb-6">
                关于我们
              </h1>
              <p className="text-foreground-500 text-center max-w-2xl mx-auto mb-12 leading-relaxed">
                {siteInfo.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-book-read-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的使命</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    为bl漫画爱好者提供高质量的阅读推荐体验，让每一部好作品都能被看见。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-star-line text-2xl text-accent-500"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">品质保证</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    每部推荐作品都经过编辑团队严格筛选，确保高分高质的阅读内容。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-heart-line text-2xl text-secondary-500"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">以用户为本</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    倾听读者声音，持续优化阅读体验，打造最懂bl爱好者的漫画平台。
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 md:p-8 mb-12">
                <h2 className="font-heading text-xl text-foreground-900 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-4">
                  <p>
                    <strong>BL漫画精选</strong>成立于{siteInfo.established}，由一群热爱bl漫画的资深读者共同创建。
                    我们深知bl漫画爱好者在海量作品中找到心仪佳作的困难，因此立志打造一个专业、可靠的bl漫画推荐平台。
                  </p>
                  <p>
                    我们的编辑团队每日精心筛选，从众多bl漫画中挑选出最优质的作品进行推荐。
                    无论是甜蜜治愈的<strong>甜宠</strong>题材、虐心催泪的<strong>虐心</strong>题材、唯美雅致的<strong>古风</strong>题材，还是贴近生活的<strong>现代</strong>题材，
                    我们都力求为读者提供最全面的bl漫画阅读选择。
                  </p>
                  <p>
                    截至目前，平台已收录超过<strong>{siteInfo.stats.totalWorks.toLocaleString()}</strong>部精选bl漫画作品，
                    每月服务超过<strong>{siteInfo.stats.monthlyActive.toLocaleString()}</strong>名活跃读者，
                    作品平均评分高达<strong>{siteInfo.stats.avgRating}</strong>分，每日更新<strong>{siteInfo.stats.dailyUpdates}</strong>部以上作品。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-100" aria-label="数据统计">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-8">平台数据</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-heading text-primary-500">{siteInfo.stats.totalWorks.toLocaleString()}+</div>
                  <div className="text-sm text-foreground-500 mt-2">精选作品</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-heading text-accent-500">{siteInfo.stats.monthlyActive.toLocaleString()}+</div>
                  <div className="text-sm text-foreground-500 mt-2">月活读者</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-heading text-primary-500">{siteInfo.stats.avgRating}</div>
                  <div className="text-sm text-foreground-500 mt-2">平均评分</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-heading text-accent-500">{siteInfo.stats.dailyUpdates}</div>
                  <div className="text-sm text-foreground-500 mt-2">每日更新</div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-label="读者口碑">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-8">读者口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="联系我们">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-4">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？欢迎随时联系我们
              </p>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-5"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的昵称
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的昵称"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="作品推荐">作品推荐</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="text-center text-sm text-green-600 bg-green-50 rounded-md py-3">
                    感谢您的留言，我们会尽快回复！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-center text-sm text-red-600 bg-red-50 rounded-md py-3">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}